// 設備頁的用電（plan SPEC NRG-V8、NRG-V5；設計稿 v06 / v06b / v07）：
//   - DeviceEnergyBlock：概覽狀態區之後的「用電」區塊（插座、Matter 插座 / 電表、接在 Station 上的攝影機）——現在功率、今天、本月到今天、估算電費、
//     〔日〕〔週〕〔月〕長條、〔匯出 CSV〕；攝影機標「由 PoE 供電量測」；不新增分頁（排行點插座 → ?tab=overview#energy）
//   - useStationEnergy / PortEnergyCells / PortWeek：Station 通道分頁的現場設備區塊每孔「今天 0.12 kWh」+ 24 小時迷你曲線（共用刻度）、點一孔展開 7 天曲線
//     （排行點 PoE 列 → ?tab=channels&port=3#site 自動展開）。沒有 energy.read：不畫 kWh 欄（即時 W 照 device.read）。
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { isKinioError } from '@kinio/web-sdk';
import { can, type VmsDevice } from '../../lib/devices';
import { useScope } from '../../lib/scope';
import { energyApi, energyMode } from './api';
import { BarChart, Legend, LineChart, Spark } from './Chart';
import { hhmm, localParts, type Fmt } from './format';
import { useEnergyEnv, useFmt } from './hooks';
import { csvKwh, downloadText, toCsv } from './model';
import { energyErrText } from './parts';
import type { SeriesPoint } from './types';
import './energy.css';

/** 這台看不看得到用電歷史：設備 actions 帶 energy.read，或家戶角色 owner / admin / member 隱含（guest 不隱含）。 */
export function canReadEnergy(dev: VmsDevice, role: string | undefined): boolean {
  return can(dev, 'energy.read') || role === 'owner' || role === 'admin' || role === 'member';
}

/** 插座的 AID 型態碼（cloud/platform internal/notify/thingname.go 的 plug 組）。 */
const PLUG_TYPES = ['PON', 'PDM', 'PMO', 'PML'];

/** 可能是量測點：插座、帶 electrical_* 能力的設備、攝影機（接在 Station 的孔上時有 PoE 供電量測；不是的話 API 回 404，不畫）。 */
export function maybeMeter(dev: VmsDevice): boolean {
  const caps = [...(dev.capabilities ?? []), ...(dev.reportedClusters ?? [])];
  return (
    dev.deviceClass === 'plug' ||
    PLUG_TYPES.includes(dev.aid.slice(0, 3)) ||
    caps.some((c) => c.startsWith('electrical_power_measurement') || c.startsWith('electrical_energy_measurement')) ||
    dev.isCamera
  );
}

type Range = 'day' | 'week' | 'month';

export function DeviceEnergyBlock({ dev }: { dev: VmsDevice }) {
  const { t } = useTranslation();
  useEnergyEnv();
  const fmt = useFmt();
  const { homes } = useScope();
  const role = homes.find((h) => h.home_id === dev.homeId)?.role;
  const allowed = canReadEnergy(dev, role) && maybeMeter(dev);
  const [range, setRange] = useState<Range>('day');
  const q = useQuery({
    queryKey: ['energy', 'device', energyMode(), dev.homeId, dev.aid, range],
    queryFn: () => energyApi.device(dev.apiEndpoint, dev.homeId, dev.aid, { period: range }),
    enabled: allowed,
    retry: false,
    staleTime: 60_000,
  });
  // 用 hash #energy 進來時捲到這一區
  useEffect(() => {
    if (q.data && location.hash === '#energy') document.getElementById('energy')?.scrollIntoView({ block: 'start' });
  }, [q.data]);
  if (!allowed) return null;
  // 不是量測點（例：攝影機沒接在 Station 的孔上）→ 不畫
  if (q.isError && isKinioError(q.error) && q.error.status === 404) return null;
  const d = q.data;
  const tz = homes.find((h) => h.home_id === dev.homeId)?.timezone;
  return (
    <section id="energy" className="ddev-section card en-root km" data-testid="ddev-energy" style={{ marginTop: 12 }}>
      <div className="en-row">
        <h2 className="section-title" style={{ margin: 0 }}>
          {t('energy:dev.title')}
        </h2>
        <span className="spacer" />
        {d && (
          <button type="button" className="btn btn-sm" onClick={() => void exportCsv(t, dev, d.series ?? [], range, tz)} data-testid="ddev-energy-csv">
            <span aria-hidden>⤓</span> {t('energy:dev.export')}
          </button>
        )}
      </div>
      {q.isLoading ? (
        <>
          <div className="skel" style={{ width: '60%' }} />
          <div className="skel" style={{ height: 120 }} />
        </>
      ) : q.isError ? (
        <p className="en-err" role="alert">
          {t('energy:dev.load_err')}（{energyErrText(t, q.error)}）
        </p>
      ) : d ? (
        <>
          {d.via_poe && <p className="en-note">{t('energy:dev.via_poe', { station: d.via_poe.station_name ?? '', port: d.via_poe.port })}</p>}
          <dl className="en-kv" style={{ margin: '8px 0' }}>
            {d.power_mw !== undefined && d.power_mw !== null && (
              <>
                <dt>{t('energy:dev.now')}</dt>
                <dd className="tabular" data-testid="ddev-energy-now">
                  {fmt.power(d.power_mw)}
                </dd>
              </>
            )}
            <dt>{t('energy:dev.today')}</dt>
            <dd className="tabular">{fmt.energy(d.today_mwh)}</dd>
            <dt>{t('energy:dev.month')}</dt>
            <dd className="tabular">{fmt.energy(d.month_mwh)}</dd>
            <dt>{t('energy:dev.cost')}</dt>
            <dd className="tabular">{d.no_tariff ? <span className="muted">{t('energy:table.no_tariff')}</span> : d.month_costs.map((m) => fmt.money(m)).join(' · ') || '—'}</dd>
          </dl>
          <div className="km-seg" role="group" aria-label={t('energy:dev.title')}>
            {(['day', 'week', 'month'] as Range[]).map((r) => (
              <button key={r} type="button" aria-pressed={range === r} onClick={() => setRange(r)}>
                {t(`energy:dev.range_${r}`)}
              </button>
            ))}
          </div>
          {d.series?.length ? <DeviceBars pts={d.series} range={range} fmt={fmt} tz={tz} /> : <p className="muted">{t('energy:dev.no_data')}</p>}
          <p className="en-note">{t('energy:card.cost_disclaimer')}</p>
        </>
      ) : null}
    </section>
  );
}

function label(t: (k: string, o?: Record<string, unknown>) => string, p: SeriesPoint, range: Range, tz?: string): string {
  const lp = localParts(p.t, tz);
  return range === 'day' ? t('energy:chart.hour_label', { h: lp.h }) : t('energy:chart.day_label', { m: lp.m, d: lp.d });
}

function DeviceBars({ pts, range, fmt, tz }: { pts: SeriesPoint[]; range: Range; fmt: Fmt; tz?: string }) {
  const { t } = useTranslation();
  const now = Date.now();
  const cells = pts.map((p) => {
    const l = label(t, p, range, tz);
    // 未來的格不畫（平台的「今天」會給到 23 時）
    if (Date.parse(p.t) > now) return { value: null, label: range === 'day' ? String(localParts(p.t, tz).h) : String(localParts(p.t, tz).d), say: '' };
    const lp = localParts(p.t, tz);
    const q = t(`energy:q.${p.quality}`);
    return {
      value: p.mwh === null ? null : p.mwh / 1e6,
      quality: p.mwh === null ? ('incomplete' as const) : p.quality,
      live: !!p.partial,
      label: range === 'day' ? String(lp.h) : String(lp.d),
      say: p.mwh === null ? t('energy:chart.say_miss', { label: l }) : p.partial ? t('energy:chart.say_live', { label: l, v: fmt.energy(p.mwh), time: hhmm(now, tz) }) : t('energy:chart.say_day', { label: l, v: fmt.energy(p.mwh), q }),
    };
  });
  const title = t(range === 'day' ? 'energy:dev.chart_day' : range === 'week' ? 'energy:dev.chart_week' : 'energy:dev.chart_month');
  return (
    <>
      <BarChart
        title={title}
        unit={fmt.unitText}
        cells={cells}
        labelEvery={range === 'day' ? 3 : range === 'month' ? 5 : 1}
        testId="ddev-energy-chart"
        table={{
          head: [t('energy:chart.col_time'), fmt.unitText, t('energy:chart.col_quality')],
          rows: pts.map((p) => [label(t, p, range, tz), p.mwh === null ? '—' : fmt.energyNum(p.mwh), t(`energy:q.${p.mwh === null ? 'incomplete' : p.quality}`)]),
          total: [t('energy:chart.total'), fmt.energyNum(pts.reduce((a, p) => a + (p.mwh ?? 0), 0)), ''],
        }}
      after={
        <Legend
          items={[
            { k: 'bar', text: t('energy:chart.legend_bar') },
            ...(cells.some((c) => c.quality === 'estimated') ? [{ k: 'est', text: t('energy:chart.legend_est') }] : []),
            ...(cells.some((c) => c.value === null) ? [{ k: 'miss', text: t('energy:chart.legend_miss') }] : []),
            ...(cells.some((c) => c.live) ? [{ k: 'live', text: t('energy:chart.legend_live') }] : []),
          ]}
        />
      }
    />
    </>
  );
}

async function exportCsv(t: (k: string, o?: Record<string, unknown>) => string, dev: VmsDevice, pts: SeriesPoint[], range: Range, tz?: string) {
  const now = new Date();
  const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const from = new Date(now.getTime() - (range === 'day' ? 1 : range === 'week' ? 7 : 30) * 86_400_000);
  const server = await energyApi.deviceCsv(dev.apiEndpoint, dev.homeId, dev.aid, range === 'day' ? 'hour' : 'day', ymd(from), ymd(now));
  const name = `${t('energy:report.csv_prefix')}-${dev.name.replace(/[/\\:*?"<>|]+/g, '_')}-${ymd(now)}.csv`;
  if (server !== null) return downloadText(name, server);
  downloadText(
    name,
    toCsv(
      t('energy:csv.disclaimer'),
      [t('energy:chart.col_time'), t('energy:csv.kwh'), t('energy:csv.quality')],
      pts.map((p) => [label(t, p, range, tz), csvKwh(p.mwh), t(`energy:q.${p.mwh === null ? 'incomplete' : p.quality}`)]),
    ),
  );
}

// ---- Station 每孔（NRG-V5） ----

export function useStationEnergy(dev: VmsDevice, enabled: boolean) {
  useEnergyEnv();
  const { homes } = useScope();
  const role = homes.find((h) => h.home_id === dev.homeId)?.role;
  const allowed = enabled && canReadEnergy(dev, role);
  return useQuery({
    queryKey: ['energy', 'station', energyMode(), dev.homeId, dev.aid],
    queryFn: () => energyApi.device(dev.apiEndpoint, dev.homeId, dev.aid, { period: 'day' }),
    enabled: allowed,
    retry: false,
    staleTime: 60_000,
  });
}

/** 一孔的「今天 0.12 kWh」+ 24 小時迷你曲線（vmax 是整台共用刻度）。 */
export function PortEnergyCell({ port, today, spark, vmax, fmt }: { port: number; today: number | null; spark: (number | null)[]; vmax: number; fmt: Fmt }) {
  const { t } = useTranslation();
  return (
    <span className="en-row" style={{ gap: 8, flexWrap: 'nowrap' }} data-testid="poe-port-energy">
      <span className="tabular nw">{today === null ? <span className="muted">{t('energy:stn.unknown')}</span> : t('energy:stn.today', { v: fmt.energy(today) })}</span>
      {spark.length > 0 && <Spark values={spark.map((v) => (v === null ? null : v / 1e6))} vmax={vmax / 1e6} label={t('energy:stn.spark', { port })} />}
    </span>
  );
}

/** 展開一孔：最近 7 天每小時供電（LineChart）。 */
export function PortWeek({ dev, port, meter, name }: { dev: VmsDevice; port: number; meter: string; name?: string }) {
  const { t } = useTranslation();
  const fmt = useFmt();
  const { homes } = useScope();
  const tz = homes.find((h) => h.home_id === dev.homeId)?.timezone;
  const now = new Date();
  const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const from = ymd(new Date(now.getTime() - 6 * 86_400_000));
  const q = useQuery({
    queryKey: ['energy', 'station-port', energyMode(), dev.homeId, meter, from],
    queryFn: () => energyApi.homeSeries(dev.apiEndpoint, dev.homeId, 'hour', from, ymd(now), [meter]),
    retry: false,
    staleTime: 60_000,
  });
  if (q.isLoading) return <div className="skel" style={{ height: 120 }} />;
  if (q.isError) return <p className="en-err">{t('energy:stn.load_err')}</p>;
  const pts = (q.data?.points ?? []).filter((p) => Date.parse(p.t) <= Date.now());
  // 平均功率（W）= 每小時 mWh ÷ 1000
  const values = pts.map((p) => (p.mwh === null ? null : p.mwh / 1000));
  const labels = pts.map((p, i) => {
    const lp = localParts(p.t, tz);
    return lp.h === 0 || i === pts.length - 1 ? (i === pts.length - 1 ? hhmm(p.t, tz) : `${lp.m}/${lp.d}`) : '';
  });
  return (
    <div className="en-root" data-testid="poe-port-week">
      <LineChart
        title={t('energy:stn.week_title', { port })}
        unit="W"
        labels={labels}
        series={[{ name: name ?? '', color: 1, values }]}
        partialLast
        table={{
          head: [t('energy:chart.col_time'), 'W'],
          rows: pts.filter((_, i) => i % 6 === 0).map((p) => [`${localParts(p.t, tz).m}/${localParts(p.t, tz).d} ${hhmm(p.t, tz)}`, p.mwh === null ? '—' : fmt.watts(p.mwh / 1000)]),
        }}
      />
    </div>
  );
}
